import { useEffect, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useNavigate, useParams } from 'react-router';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n';
import { Button } from '../components/ui/Button';
import { ArrowForward, ChevronBack, Close, SignOut } from '../components/ui/icons';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import { displayFirstName, initials } from '../lib/names';
import { navigateWithTransition, supportsViewTransitions } from '../lib/navigation';
import { useSession } from '../lib/session';
import { isSettingsSection, type SettingsSectionId } from '../lib/settings-sections';
import { isIosSafariBrowser } from '../lib/viewport';
import { api, type HealthInfo } from '../lib/api';
import { ActivityScreen } from './Activity';
import { ConnectionsScreen } from './Connections';
import { MemoryScreen } from './Memory';
import { PermissionsScreen } from './Permissions';
import { PrivacyScreen } from './Privacy';
import { SecurityScreen } from './Security';

/** The You destination: what TWO knows, can access, can do, has done, and your account. */
export function YouLayout() {
  return (
    <section className="scroller flex min-h-0 flex-1 flex-col">
      <Outlet />
    </section>
  );
}

const GROUPS: { key: string; to: string; section?: SettingsSectionId }[] = [
  { key: 'knows', to: '/you/memory', section: 'memory' },
  { key: 'access', to: '/you/connections', section: 'connections' },
  { key: 'can', to: '/you/permissions', section: 'permissions' },
  { key: 'done', to: '/you/activity', section: 'activity' },
];

/** A link into a sub-page: slides in from the end (View Transitions, CSS fallback). */
function useSubPageLink() {
  const navigate = useNavigate();
  return (to: string) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigateWithTransition(navigate, to, 'forward');
  };
}

/** Fallback entrance for browsers without the View Transitions API. */
const SUB_PAGE = supportsViewTransitions() ? '' : 'animate-slide-in';

export function YouIndex() {
  const { t } = useTranslation();
  const { me } = useSession();
  const name = displayFirstName(me?.first_name) || me?.display_name || '';
  const go = useSubPageLink();
  const [memoryCount, setMemoryCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .memories()
      .then((r) => {
        if (!cancelled) setMemoryCount(r.count);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col gap-6 p-4">
      <header className="flex items-center gap-3">
        <span
          aria-hidden
          className="flex size-12 items-center justify-center rounded-full bg-brand-soft text-base font-semibold text-ink"
        >
          {initials(me?.first_name ?? me?.display_name, me?.last_name)}
        </span>
        <div className="min-w-0">
          <h2 className="truncate text-xl font-semibold tracking-tight" dir="auto">
            {name}
          </h2>
          <p className="truncate text-sm text-ink-2" dir="ltr">
            {me?.email ?? ''}
          </p>
        </div>
      </header>

      <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface shadow-sm">
        {GROUPS.map((g) => (
          <li key={g.key}>
            <Link
              to={g.to}
              onClick={g.section ? go(g.to) : undefined}
              className="flex min-h-14 items-center gap-3 px-4 py-3 transition-colors hover:bg-canvas"
            >
              <span className="flex flex-1 flex-col gap-0.5">
                <span className="text-base font-medium text-ink">
                  {t(`you.groups.${g.key}.title`)}
                  {g.key === 'knows' && memoryCount ? (
                    <span className="tabular text-ink-2"> · {memoryCount}</span>
                  ) : null}
                </span>
                <span className="text-sm text-ink-2">{t(`you.groups.${g.key}.hint`)}</span>
              </span>
              <ArrowForward size={18} className="shrink-0 text-ink-3" />
            </Link>
          </li>
        ))}
      </ul>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-ink-2">{t('you.groups.account.title')}</h3>
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-surface shadow-sm">
          {(['security', 'privacy'] as const).map((id) => (
            <li key={id}>
              <Link
                to={`/you/${id}`}
                onClick={go(`/you/${id}`)}
                className="flex min-h-12 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-canvas"
              >
                <span className="flex-1 text-base text-ink">{t(`settings.sections.${id}`)}</span>
                <ArrowForward size={18} className="shrink-0 text-ink-3" />
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/you/account"
              onClick={go('/you/account')}
              className="flex min-h-12 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-canvas"
            >
              <span className="flex-1 text-base text-ink">{t('you.account')}</span>
              <ArrowForward size={18} className="shrink-0 text-ink-3" />
            </Link>
          </li>
        </ul>
      </div>
    </div>
  );
}

const A2HS_DISMISSED_KEY = 'pa.a2hs_dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(A2HS_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * "Add TWO to your Home Screen": a small dismissible card, only on iOS Safari when the app is
 * not installed, only here (never on first launch, never a modal).
 */
function AddToHomeScreenHint() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => isIosSafariBrowser() && !readDismissed());
  if (!visible) return null;
  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(A2HS_DISMISSED_KEY, '1');
    } catch {
      // per-browser convenience only
    }
  };
  return (
    <div
      role="note"
      className="flex items-start gap-3 rounded-lg border border-line bg-brand-soft px-4 py-3 text-sm text-ink"
    >
      <div className="flex-1">
        <p className="font-medium">{t('you.addToHome.title')}</p>
        <p className="mt-0.5 text-ink-2">{t('you.addToHome.body')}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t('you.addToHome.dismiss')}
        className="-me-2 -mt-1 flex size-10 shrink-0 items-center justify-center rounded-md text-ink-2 hover:text-ink"
      >
        <Close size={18} />
      </button>
    </div>
  );
}

/**
 * M5 account settings: "Reach me by email when I am away" (default off) and the daily brief
 * with its local time (default 07:30). Saved at once; the person's alarm follows.
 */
function AccountSettings() {
  const { t } = useTranslation();
  const { me, refresh } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const settings = me?.settings ?? { away_email: false, brief_enabled: false, brief_time: '07:30' };
  const save = async (
    key: string,
    input: { away_email?: boolean; brief_enabled?: boolean; brief_time?: string },
  ) => {
    setBusy(key);
    setFailed(false);
    try {
      await api.updateSettings(input);
      await refresh();
    } catch {
      setFailed(true);
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4 shadow-sm">
      <label className="flex min-h-11 cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 size-5 shrink-0 accent-[var(--pa-brand)]"
          checked={settings.away_email}
          disabled={busy !== null}
          onChange={(e) => void save('away_email', { away_email: e.target.checked })}
        />
        <span className="flex flex-col gap-0.5">
          <span className="text-base text-ink">{t('you.awayEmail.title')}</span>
          <span className="text-sm text-ink-2">{t('you.awayEmail.hint')}</span>
        </span>
      </label>
      <label className="flex min-h-11 cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 size-5 shrink-0 accent-[var(--pa-brand)]"
          checked={settings.brief_enabled}
          disabled={busy !== null}
          onChange={(e) => void save('brief_enabled', { brief_enabled: e.target.checked })}
        />
        <span className="flex flex-col gap-0.5">
          <span className="text-base text-ink">{t('you.brief.title')}</span>
          <span className="text-sm text-ink-2">{t('you.brief.hint')}</span>
        </span>
      </label>
      {settings.brief_enabled ? (
        <label className="flex items-center justify-between gap-3 ps-8">
          <span className="text-sm text-ink-2">{t('you.brief.time')}</span>
          <input
            type="time"
            value={settings.brief_time}
            disabled={busy !== null}
            onChange={(e) => {
              if (/^\d{2}:\d{2}$/u.test(e.target.value)) {
                void save('brief_time', { brief_time: e.target.value });
              }
            }}
            className="control h-12 rounded-md border border-line-strong bg-surface px-3 text-base text-ink"
          />
        </label>
      ) : null}
      {failed ? (
        <p className="text-sm text-critical-text" dir="auto">
          {t('common.error')}
        </p>
      ) : null}
    </div>
  );
}

/** "Build <short sha> · <deploy time UTC>" for the web and the API, plus the contact line (M5.2). */
function BuildFooter() {
  const { t } = useTranslation();
  const [health, setHealth] = useState<HealthInfo | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .health()
      .then((h) => {
        if (!cancelled) setHealth(h);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  const utc = (iso: string | null | undefined) =>
    iso ? `${iso.slice(0, 16).replace('T', ' ')} UTC` : '';
  return (
    <footer className="mt-2 flex flex-col gap-0.5 text-xs text-ink-3" data-build-footer>
      <p className="tabular" dir="ltr">
        {t('you.build', { build: __BUILD_ID__, time: utc(__BUILD_TIME__) })}
      </p>
      {health ? (
        <p className="tabular" dir="ltr">
          {t('you.buildApi', { build: health.build, time: utc(health.deployed_at) })}
        </p>
      ) : null}
      <p dir="ltr">{t('you.contact')}</p>
    </footer>
  );
}

/** /you/account: identity summary, language, the M5 settings, Home Screen hint and sign out. */
export function YouAccount() {
  const { t, i18n } = useTranslation();
  const { me, signOut } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const current = (i18n.resolvedLanguage ?? 'en') as SupportedLanguage;
  return (
    <div className={['flex flex-col gap-4 p-4', SUB_PAGE].join(' ')}>
      <BackLink label={t('you.back')} />
      <h2 className="text-xl font-semibold tracking-tight">{t('you.account')}</h2>
      <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
        <p className="text-base text-ink" dir="auto">
          {me?.display_name}
        </p>
        <p className="text-sm text-ink-2" dir="ltr">
          {me?.email ?? ''}
        </p>
      </div>

      <div className="rounded-lg border border-line bg-surface p-4 shadow-sm">
        <p className="mb-2 text-sm font-medium text-ink">{t('you.language')}</p>
        <SegmentedControl<SupportedLanguage>
          label={t('you.language')}
          value={current}
          onChange={(lng) => void i18n.changeLanguage(lng)}
          options={SUPPORTED_LANGUAGES.map((lng) => ({ value: lng, label: t(`language.${lng}`) }))}
        />
      </div>

      <AccountSettings />

      <AddToHomeScreenHint />

      <Button
        variant="secondary"
        loading={busy}
        leading={<SignOut size={18} />}
        onClick={() => {
          setBusy(true);
          void signOut().finally(() => navigate('/signin', { replace: true }));
        }}
      >
        {t('nav.signout')}
      </Button>
      <BuildFooter />
    </div>
  );
}

/** Sub-pages behind the relationship copy: honest empty states, never "placeholder". */
export function YouSection() {
  const { t } = useTranslation();
  const { section } = useParams();
  if (section === 'account') return <YouAccount />;
  if (!isSettingsSection(section)) return <Navigate to="/you" replace />;
  return (
    <div className={['p-4', SUB_PAGE].join(' ')}>
      <BackLink label={t('you.back')} />
      <h2 className="mt-2 text-xl font-semibold tracking-tight">
        {t(`settings.sections.${section}`)}
      </h2>
      <p className="mt-1 text-base text-ink-2">{t(`settings.descriptions.${section}`)}</p>
      {section === 'memory' ? (
        <MemoryScreen />
      ) : section === 'connections' ? (
        <ConnectionsScreen />
      ) : section === 'permissions' ? (
        <PermissionsScreen />
      ) : section === 'activity' ? (
        <ActivityScreen />
      ) : section === 'security' ? (
        <SecurityScreen />
      ) : section === 'privacy' ? (
        <PrivacyScreen />
      ) : (
        <div
          role="status"
          className="mt-6 rounded-lg border border-line bg-surface p-6 text-center text-sm text-ink-2 dark:bg-elevated"
        >
          {t(`settings.empty.${section}`, { defaultValue: t('settings.empty.generic') })}
        </div>
      )}
    </div>
  );
}

/** 44px chevron plus label; slides the page out on the way back. */
function BackLink({ label }: { label: string }) {
  const navigate = useNavigate();
  return (
    <Link
      to="/you"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigateWithTransition(navigate, '/you', 'back');
      }}
      className="-ms-3 inline-flex min-h-11 items-center gap-1 self-start rounded-md pe-3 text-sm font-medium text-ink-2 hover:text-ink"
    >
      <span className="flex size-11 items-center justify-center">
        <ChevronBack size={22} />
      </span>
      {label}
    </Link>
  );
}
